import { JobState } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { FaDownload } from 'react-icons/fa';
import { useAppContext } from '../../hooks/useAppContext';
import type { IncidentsFilter } from '../../hooks/useIncidentsFilter';
import { useIncidentsExportJob } from '../../hooks/useJob';
import { usePermission } from '../../hooks/usePermission';

interface Props {
  filter: IncidentsFilter;
}

export const IncidentExportMenuItem = (props: Props) => {
  const { filter } = props;
  const { organization, promiseToast } = useAppContext();

  const hasPermission = usePermission('read', 'incident');

  const exportJob = useIncidentsExportJob();

  const onClick = React.useCallback(async () => {
    const promise = async (): Promise<string> => {
      const job = await exportJob.start({
        organizationId: organization.id,
        request: {
          start: filter.startDateFilter,
          end: filter.endDateFilter,
          labels: filter.labelFilter,
          location: filter.locationFilter,
          participant: filter.participantFilter,
          severity: filter.severityFilter,
          status: filter.statusFilter,
        },
      });

      if (job?.state !== JobState.Completed) {
        throw new Error('Incidents export was not generated');
      }

      return job.response.url;
    };

    await promiseToast(promise, {
      loading: {
        title: 'Generating incidents export...',
      },
      success: (url) => ({
        title: 'Incidents export successfully generated',
        description: 'Your archive is ready. Download it before closing this message.',
        duration: Number.POSITIVE_INFINITY,
        closable: true,
        action: {
          label: 'Download',
          onClick: () => window.open(url, '_self'),
        },
      }),
      error: {
        title: 'Error exporting incidents',
      },
    });
  }, [exportJob, organization.id, filter, promiseToast]);

  return (
    <ChakraV3.MenuItem onClick={onClick} value='export-incidents' disabled={!hasPermission}>
      <FaDownload />
      Export incidents (ZIP)
    </ChakraV3.MenuItem>
  );
};
